package br.com.vvrefeicoes.portal.domain.model

fun diffOrgChart(
    published: OrgChartData,
    draft: OrgChartData,
): List<OrgChartChange> {
    val changes = mutableListOf<OrgChartChange>()

    val publishedDirectors = published.directors.filter { !it.inactive }
    val draftDirectors = draft.directors.filter { !it.inactive }
    val publishedDirectorIds = publishedDirectors.map { it.id }.toSet()
    val draftDirectorIds = draftDirectors.map { it.id }.toSet()

    draftDirectors.filter { it.id !in publishedDirectorIds }.forEach { changes += OrgChartChange.DirectorAdded(it.name) }
    publishedDirectors.filter { it.id !in draftDirectorIds }.forEach { changes += OrgChartChange.DirectorRemoved(it.name) }

    val publishedManagers = published.managers.filter { !it.inactive }
    val draftManagers = draft.managers.filter { !it.inactive }
    val publishedManagerIds = publishedManagers.map { it.id }.toSet()
    val draftManagerIds = draftManagers.map { it.id }.toSet()

    draftManagers.filter { it.id !in publishedManagerIds }.forEach { manager ->
        changes += OrgChartChange.ManagerAdded(manager.name)
        directorsOfManager(draft, manager.id).forEach { changes += OrgChartChange.ManagerRespondsTo(manager.name, it.name) }
    }
    publishedManagers.filter { it.id !in draftManagerIds }.forEach { changes += OrgChartChange.ManagerRemoved(it.name) }
    draftManagers.filter { it.id in publishedManagerIds }.forEach { manager ->
        val before = directorsOfManager(published, manager.id)
        val after = directorsOfManager(draft, manager.id)
        val beforeIds = before.map { it.id }.toSet()
        val afterIds = after.map { it.id }.toSet()
        val removed = before.filter { it.id !in afterIds }
        val added = after.filter { it.id !in beforeIds }
        removed.forEach { changes += OrgChartChange.ManagerStoppedRespondingTo(manager.name, it.name) }
        when {
            added.isEmpty() -> Unit
            removed.isEmpty() -> changes += OrgChartChange.ManagerRespondsToAll(manager.name, after.map { it.name })
            else -> added.forEach { changes += OrgChartChange.ManagerRespondsTo(manager.name, it.name) }
        }
    }

    val publishedCompanies = published.companies.filter { !it.inactive }
    val draftCompanies = draft.companies.filter { !it.inactive }
    val publishedCompanyIds = publishedCompanies.map { it.id }.toSet()
    val draftCompanyIds = draftCompanies.map { it.id }.toSet()

    draftCompanies.filter { it.id !in publishedCompanyIds }.forEach { company ->
        changes += OrgChartChange.CompanyAdded(company.name)
        changes += addedLinkChanges(company.name, draft.companyLinks[company.id] ?: emptySet(), published, draft)
    }
    publishedCompanies.filter { it.id !in draftCompanyIds }.forEach { changes += OrgChartChange.CompanyRemoved(it.name) }
    draftCompanies.filter { it.id in publishedCompanyIds }.forEach { company ->
        val before = published.companyLinks[company.id] ?: emptySet()
        val after = draft.companyLinks[company.id] ?: emptySet()
        val managersBefore = before.mapNotNull { it.managerId }.toSet()
        val managersAfter = after.mapNotNull { it.managerId }.toSet()
        val directorsBefore = before.map { it.directorId }.filter { it.isNotEmpty() }.toSet()
        val directorsAfter = after.map { it.directorId }.filter { it.isNotEmpty() }.toSet()
        val managerChanged = managersBefore != managersAfter
        val directorChanged = directorsBefore != directorsAfter
        when {
            managerChanged && !directorChanged -> {
                (managersBefore - managersAfter).forEach {
                    changes += OrgChartChange.CompanyStoppedBeingManagedBy(company.name, managerName(published, draft, it))
                }
                (managersAfter - managersBefore).forEach {
                    changes += OrgChartChange.CompanyManagedBy(company.name, listOf(managerName(published, draft, it)))
                }
            }
            directorChanged && !managerChanged -> {
                val removed = directorsBefore - directorsAfter
                val added = directorsAfter - directorsBefore
                removed.forEach { changes += OrgChartChange.CompanyUnlinkedFromDirector(company.name, directorName(published, draft, it)) }
                when {
                    added.isEmpty() -> Unit
                    removed.isEmpty() ->
                        changes += OrgChartChange.CompanyLinkedToDirectors(company.name, directorsAfter.map { directorName(published, draft, it) })
                    else ->
                        added.forEach {
                            changes += OrgChartChange.CompanyLinkedToDirectors(company.name, listOf(directorName(published, draft, it)))
                        }
                }
            }
            else -> {
                (before - after).forEach { changes += linkChange(company.name, removed = true, it, published, draft) }
                (after - before).forEach { changes += linkChange(company.name, removed = false, it, published, draft) }
            }
        }
    }

    return changes
}

private fun addedLinkChanges(
    name: String,
    links: Set<CompanyLink>,
    published: OrgChartData,
    draft: OrgChartData,
): List<OrgChartChange> {
    val changes = mutableListOf<OrgChartChange>()
    links
        .filter { it.managerId != null }
        .groupBy { it.managerId.orEmpty() }
        .forEach { (managerId, group) ->
            val directors =
                group
                    .mapNotNull { if (it.directorId.isEmpty()) null else directorName(published, draft, it.directorId) }
                    .distinct()
            val manager = managerName(published, draft, managerId)
            changes +=
                if (directors.isEmpty()) {
                    OrgChartChange.CompanyManagedBy(name, listOf(manager))
                } else {
                    OrgChartChange.CompanyManagedByInDirectors(name, manager, directors)
                }
        }
    val directs =
        links
            .filter { it.managerId == null }
            .mapNotNull { if (it.directorId.isEmpty()) null else directorName(published, draft, it.directorId) }
            .distinct()
    if (directs.isNotEmpty()) {
        changes += OrgChartChange.CompanyLinkedToDirectors(name, directs)
    }
    return changes
}

private fun linkChange(
    name: String,
    removed: Boolean,
    link: CompanyLink,
    published: OrgChartData,
    draft: OrgChartData,
): OrgChartChange =
    when {
        link.managerId != null && link.directorId.isEmpty() ->
            if (removed) {
                OrgChartChange.CompanyStoppedBeingManagedBy(name, managerName(published, draft, link.managerId))
            } else {
                OrgChartChange.CompanyManagedBy(name, listOf(managerName(published, draft, link.managerId)))
            }
        link.managerId != null ->
            if (removed) {
                OrgChartChange.CompanyStoppedBeingManagedByInDirectors(name, managerName(published, draft, link.managerId), listOf(directorName(published, draft, link.directorId)))
            } else {
                OrgChartChange.CompanyManagedByInDirectors(name, managerName(published, draft, link.managerId), listOf(directorName(published, draft, link.directorId)))
            }
        link.directorId.isEmpty() ->
            if (removed) {
                OrgChartChange.CompanyUnlinkedFromOrganogram(name)
            } else {
                OrgChartChange.CompanyLinkedToOrganogram(name)
            }
        else ->
            if (removed) {
                OrgChartChange.CompanyUnlinkedFromDirector(name, directorName(published, draft, link.directorId))
            } else {
                OrgChartChange.CompanyLinkedToDirectors(name, listOf(directorName(published, draft, link.directorId)))
            }
    }

private fun directorsOfManager(
    data: OrgChartData,
    managerId: String,
): List<Director> = data.directors.filter { !it.inactive && it.id in (data.managerDirectors[managerId] ?: emptySet()) }

private fun managerName(
    published: OrgChartData,
    draft: OrgChartData,
    managerId: String,
): String =
    draft.managers.firstOrNull { it.id == managerId }?.name
        ?: published.managers.firstOrNull { it.id == managerId }?.name
        ?: managerId

private fun directorName(
    published: OrgChartData,
    draft: OrgChartData,
    directorId: String,
): String =
    draft.directors.firstOrNull { it.id == directorId }?.name
        ?: published.directors.firstOrNull { it.id == directorId }?.name
        ?: directorId
